import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Chip,
  Link,
  Stack,
  Typography,
} from '@mui/material'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import { PageSeo } from './page-seo'
import { ATTRIBUTION_DATA } from './attribution-data'

const ATTRIBUTIONS_SCHEMA = {
  '@context': 'https://schema.org',
  '@type': 'WebPage',
  name: 'Open Source Notices · TerraPlanner',
  url: 'https://terra-planner.com/attributions',
}

const LICENSE_CHIP_COLOR = {
  MIT: 'success',
  'BSD-2-Clause': 'info',
  'BSD-3-Clause': 'info',
  'Apache-2.0': 'warning',
  ISC: 'success',
  'Hippocratic-2.1': 'secondary',
  'Mapbox Custom': 'error',
}

export const Attributions = () => {
  return (
    <Box sx={{ px: { xs: 3, md: 6 }, py: { xs: 6, md: 8 }, maxWidth: 860, mx: 'auto' }}>
      <PageSeo
        title="Open Source Notices"
        description="TerraPlanner is built on great open-source tools. We gratefully acknowledge all primary dependencies and their licences."
        path="/attributions"
        schema={ATTRIBUTIONS_SCHEMA}
      />

      <Typography component="h1" variant="h4" fontWeight={700} gutterBottom>
        Open Source Notices
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mt: 1, mb: 4, lineHeight: 1.75 }}>
        TerraPlanner is built on the shoulders of great open-source and commercial tools.
        We gratefully acknowledge the following primary dependencies and their authors.
        Expand any entry to read the full license text.
      </Typography>

      {ATTRIBUTION_DATA.map((entry) => (
        <Accordion key={entry.name} disableGutters elevation={0} sx={{ border: '1px solid', borderColor: 'divider', '&:not(:last-child)': { borderBottom: 0 }, '&::before': { display: 'none' }, '&:first-of-type': { borderRadius: '8px 8px 0 0' }, '&:last-of-type': { borderRadius: '0 0 8px 8px' } }}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ px: 3, py: 1.5 }}>
            <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap" sx={{ gap: 1 }}>
              {entry.repository ? (
                <Link
                  href={entry.repository}
                  target="_blank"
                  rel="noopener noreferrer"
                  underline="hover"
                  fontWeight={600}
                  color="primary"
                  onClick={(e) => e.stopPropagation()}
                >
                  {entry.name}
                </Link>
              ) : (
                <Typography fontWeight={600} color="primary.main">{entry.name}</Typography>
              )}
              <Typography variant="caption" color="text.secondary">
                v{entry.version}
              </Typography>
              <Chip
                label={entry.licenseType}
                size="small"
                color={LICENSE_CHIP_COLOR[entry.licenseType] ?? 'default'}
                variant="outlined"
                sx={{ fontSize: '0.65rem', height: 20 }}
              />
            </Stack>
          </AccordionSummary>
          <AccordionDetails sx={{ px: 3, pb: 3 }}>
            <Box
              component="pre"
              sx={{
                fontFamily: 'monospace',
                fontSize: '0.75rem',
                lineHeight: 1.7,
                color: 'text.secondary',
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
                m: 0,
                p: 2,
                bgcolor: 'action.hover',
                borderRadius: 1,
              }}
            >
              {entry.licenseText}
            </Box>
          </AccordionDetails>
        </Accordion>
      ))}
    </Box>
  )
}
